import {
  collection,
  doc,
  getDocs,
  query,
  orderBy,
  arrayUnion,
  updateDoc,
  getDoc,
  where,
  onSnapshot,
  setDoc,
  deleteDoc,
  addDoc,
  limit,
  startAfter,
  getCountFromServer,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";
import { firestored, storage } from "../firebase/firebaseConfig";
import {
  getAuth,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from "firebase/auth";
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  uploadBytes,
  deleteObject,
} from "firebase/storage";
import {
  computeNewReactionState,
  applyReactionDeltas,
  EMPTY_REACTION_COUNTS,
} from "./feedHelpers";
import { generateFeedbackToken } from "./openHouseHelpers";

const auth = getAuth();

export const saveuserdata = async (data, uid) => {
  return await setDoc(doc(firestored, "users", `${uid}`), data, { merge: true })
    .then(() => {
      return "success";
    })
    .catch((err) => {
      return err;
    });
};

export const userlogin = async (email, password) => {
  return await signInWithEmailAndPassword(auth, email, password)
    .then((userCredential) => {
      // Signed in
      const user = userCredential.user;
      return user;
      // ...
    })
    .catch((err) => {
      return err;
    });
};

// Soft-archive a deal: hidden from the board by default; restore by setting false.
export const setDealArchived = async (dealId, archived) => {
  const docRef = doc(firestored, "deals", String(dealId));
  await setDoc(docRef, { archived: !!archived }, { merge: true });
  return true;
};

// Soft delete: mark the deal as deleted with a timestamp. Kept for 30 days
// in the Recently Deleted view, then can be permanently purged. Photos are
// preserved so a restore brings the deal back intact.
export const deleteDealWithPhotos = async (dealId) => {
  try {
    const docRef = doc(firestored, "deals", String(dealId));
    await setDoc(docRef, { deletedAt: Date.now() }, { merge: true });
    return true;
  } catch (error) {
    console.error("🚨 Error soft-deleting deal:", error);
    return false;
  }
};

// Clear the deletedAt flag — brings a soft-deleted deal back into the main list.
export const restoreDeal = async (dealId) => {
  try {
    const docRef = doc(firestored, "deals", String(dealId));
    // Firestore can't unset via setDoc merge with undefined — write null and
    // treat null/0/missing as "not deleted" in the read filters.
    await setDoc(docRef, { deletedAt: null }, { merge: true });
    return true;
  } catch (error) {
    console.error("🚨 Error restoring deal:", error);
    return false;
  }
};

// Permanent destroy — used by the Recently Deleted view when the user clicks
// "Delete forever" or by the 30-day auto-purge. This is the original
// destructive behavior.
export const permanentlyDeleteDeal = async (dealId) => {
  try {
    const dealsCollection = collection(firestored, "deals");
    const q = query(dealsCollection, where("createdat", "==", dealId));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) return false;

    for (const dealDoc of querySnapshot.docs) {
      const dealData = dealDoc.data();
      if (Array.isArray(dealData.propertyphoto)) {
        for (const photoUrl of dealData.propertyphoto) {
          try {
            await deleteObject(ref(storage, photoUrl));
          } catch (imgErr) {
            console.warn("⚠️ Image delete failed:", photoUrl, imgErr);
          }
        }
      }
      await deleteDoc(doc(firestored, "deals", dealDoc.id));
    }
    return true;
  } catch (error) {
    console.error("🚨 Error permanently deleting deal:", error);
    return false;
  }
};

// Return only deals that have been soft-deleted (deletedAt is a number > 0).
export const getDeletedDeals = async () => {
  const items = await getDocs(collection(firestored, "deals"));
  return items.docs
    .map((d) => d.data())
    .filter((d) => typeof d.deletedAt === "number" && d.deletedAt > 0)
    .sort((a, b) => (b.deletedAt || 0) - (a.deletedAt || 0));
};

export const fetchUserById = async (id) => {
  try {
    console.log(`🔍 Fetching blog with user id: ${id}`);

    const blogsCollection = collection(firestored, "users");
    const q = query(blogsCollection, where("id", "==", id)); // Filter blogsId
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      console.log("❌ No such document in Firestore!");
      return null;
    }

    let userData = null;
    querySnapshot.forEach((doc) => {
      userData = doc.data();
    });

    console.log("✅ Blog found:", userData);
    return userData;
  } catch (error) {
    console.error("🚨 Error fetching blog:", error);
    return null;
  }
};

export const updateUserProfile = async (userId, updatedData) => {
  try {
    const userRef = doc(firestored, "users", userId);
    await updateDoc(userRef, updatedData);
    return true;
  } catch (error) {
    console.error("Error updating profile:", error);
    return false;
  }
};

export const updateAccessUser = async (userId, updatedData) => {
  try {
    const userRef = doc(firestored, "users", userId);
    await updateDoc(userRef, updatedData);
    return true;
  } catch (error) {
    console.error("Error updating profile:", error);
    return false;
  }
};

export const changeUserPassword = async (
  email,
  currentPassword,
  newPassword,
) => {
  const auth = getAuth();
  const user = auth.currentUser;

  // Create credential
  const credential = EmailAuthProvider.credential(email, currentPassword);

  // Step 1: Re-authenticate
  await reauthenticateWithCredential(user, credential);

  // Step 2: Update password
  await updatePassword(user, newPassword);
};

export const uploadUserImage = async (file, userId) => {
  try {
    const storageRef = ref(storage, `profilePictures/${userId}/${file.name}`);
    await uploadBytes(storageRef, file);
    return await getDownloadURL(storageRef);
  } catch (error) {
    console.error("Error uploading image:", error);
    return null;
  }
};

export const getUser = async () => {
  try {
    const querySnapshot = await getDocs(collection(firestored, "users"));
    const data = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    console.log("users data", data);
    return data;
  } catch (error) {
    console.error("Error fetching users: ", error);
    return [];
  }
};

export const getHelpElker = async () => {
  try {
    const querySnapshot = await getDocs(collection(firestored, "helpElker"));
    const data = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    console.log("helpElker data", data);
    return data;
  } catch (error) {
    console.error("Error fetching helpElker: ", error);
    return [];
  }
};

// export const getuserinformation = async (collection, uid) => {
//   try {
//     const userRef = ref(firestored, `${collection}/${uid}`);
//     const snapshot = await get(userRef);

//     if (snapshot.exists()) {
//       console.log("User exists in Firebase:", snapshot.val());
//       return snapshot.val();
//     } else {
//       console.warn("User not found in Firebase!");
//       return null;
//     }
//   } catch (error) {
//     console.error("Error fetching user data:", error);
//     return null;
//   }
// };

export const getuserinformation = async (collectionname, documentId) => {
  try {
    const docRef = doc(firestored, collectionname, documentId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      console.log("User Data:", docSnap.data());
      return docSnap.data();
    } else {
      console.log("No such document!");
      return null;
    }
  } catch (error) {
    console.error("Error fetching document:", error);
    return null;
  }
};

export const getAllUserItems = async (table, parameter, comparison, value) => {
  const items = await getDocs(
    query(collection(firestored, table), where(parameter, comparison, value)),
  );

  return items.docs.map((doc) => doc.data());
};

export const getAllSlotsWhere = async (
  table,
  parameter,
  comparison,
  value,
  parameter1,
  comparison1,
  value1,
  parameter2,
  comparison2,
  value2,
) => {
  const items = await getDocs(
    query(
      collection(firestored, table),
      where(parameter, comparison, value),
      where(parameter1, comparison1, value1),
      where(parameter2, comparison2, value2),
    ),
  );

  return items.docs.map((doc) => doc.data());
};

export const getAllSlotsWherex4 = async (
  table,
  parameter,
  comparison,
  value,
  parameter1,
  comparison1,
  value1,
  parameter2,
  comparison2,
  value2,
  parameter3,
  comparison3,
  value3,
) => {
  const items = await getDocs(
    query(
      collection(firestored, table),
      where(parameter, comparison, value),
      where(parameter1, comparison1, value1),
      where(parameter2, comparison2, value2),
      where(parameter3, comparison3, value3),
    ),
  );

  return items.docs.map((doc) => doc.data());
};
export const getAllSlotsWherex3 = async (
  table,
  parameter,
  comparison,
  value,
  parameter1,
  comparison1,
  value1,
  parameter2,
  comparison2,
  value2,
  parameter3,
  comparison3,
  value3,
) => {
  const items = await getDocs(
    query(
      collection(firestored, table),
      where(parameter, comparison, value),
      where(parameter1, comparison1, value1),
      where(parameter1, comparison1, value1),
    ),
  );

  return items.docs.map((doc) => doc.data());
};

export const getAllSlotsForChecking = async (
  table,
  parameter,
  comparison,
  value,
  parameter1,
  comparison1,
  value1,
) => {
  const items = await getDocs(
    query(
      collection(firestored, table),
      where(parameter, comparison, value),
      where(parameter1, comparison1, value1),
      where("status", "==", "Accepted"),
    ),
  );

  return items.docs.map((doc) => doc.data());
};

export const getAllEmployeeandSupervisors = async (
  table,
  parameter,
  comparison,
  value,
  value2,
) => {
  const items = await getDocs(
    query(
      collection(firestored, table),
      where(parameter, "in", [value, value2]),
    ),
  );

  return items.docs.map((doc) => doc.data());
};

export const getAllEmployeeandSupervisorsforclient = async (
  table,
  parameter,
  comparison,
  value,
  value2,
  clientid,
) => {
  const items = await getDocs(
    query(
      collection(firestored, table),
      where(parameter, "in", [value, value2]),
      where("createdby", "==", clientid),
    ),
  );

  return items.docs.map((doc) => doc.data());
};

export const getAllApprovedleaves = async (table, parameter, data) => {
  const items = await getDocs(
    query(collection(firestored, table), where(parameter, "in", data)),
  );

  return items.docs.map((doc) => doc.data());
};

export const getAllCleanerShifts = async (table, cleanerid, date) => {
  const items = await getDocs(
    query(
      collection(firestored, table),
      where("cleaningshiftid", "==", cleanerid),
      where("sheduleddate", "==", date),
    ),
  );

  return items.docs.map((doc) => doc.data());
};

export const getAllEmployeeandSupervisorsforsupervisor = async (
  table,
  parameter,
  comparison,
  value,
  value2,
  creatorid,
) => {
  const items = await getDocs(
    query(
      collection(firestored, table),
      where(parameter, "in", [value, value2]),
      where("createdby", "==", creatorid),
    ),
  );

  return items.docs.map((doc) => doc.data());
};

export const getAllEmployeeForProject = async (
  table,
  parameter,
  comparison,
  value,
  projectid,
) => {
  const items = await getDocs(
    query(
      collection(firestored, table),
      where(parameter, comparison, value),
      where("projectassigned", comparison, projectid),
    ),
  );

  return items.docs.map((doc) => doc.data());
};

export const getAllEmployeeForClient = async (
  table,
  parameter,
  comparison,
  value,
  projectid,
) => {
  const items = await getDocs(
    query(
      collection(firestored, table),
      where(parameter, comparison, value),
      where("createdby", comparison, projectid),
    ),
  );

  return items.docs.map((doc) => doc.data());
};

export const uploadImagesToFirebase = async (files) => {
  const uploadedUrls = [];

  for (let file of files) {
    const fileName = `dealPhotos/${Date.now()}-${file.name}`;
    const storageRef = ref(storage, fileName);

    const uploadTask = await uploadBytesResumable(storageRef, file);

    const downloadUrl = await getDownloadURL(uploadTask.ref);
    uploadedUrls.push(downloadUrl);
  }

  return uploadedUrls; // <-- array of URLs
};

export const saveitemwithid = async (table, id, data) => {
  return await setDoc(doc(firestored, table, id), data, { merge: true })
    .then(() => {
      return "success";
    })
    .catch((err) => {
      return err;
    });
};

export const getDealById = async (id) => {
  try {
    const dealRef = doc(firestored, "deals", id);
    const dealSnap = await getDoc(dealRef);

    if (dealSnap.exists()) {
      return { id: dealSnap.id, ...dealSnap.data() };
    } else {
      throw new Error("recipe not found");
    }
  } catch (error) {
    console.error("Error fetching recipe:", error);
    throw error;
  }
};

export const updateItem = async (table, id, data) => {
  return await setDoc(doc(firestored, table, id.toString()), data, {
    merge: true,
  })
    .then(() => {
      return "success";
    })
    .catch((err) => {
      return err;
    });
};

export const updateNestedItem = async (
  table,
  id,
  innertable,
  data,
  innerid,
) => {
  return await setDoc(
    doc(firestored, `${table}/${id}/${innertable}`, innerid.toString()),
    data,
    { merge: true },
  )
    .then(() => {
      return "success";
    })
    .catch((err) => {
      return err;
    });
};
export const updateNestedArrayItem = async (
  table,
  id,
  innertable,
  data,
  innerid,
) => {
  return await setDoc(
    doc(firestored, `${table}/${id}/${innertable}`, innerid.toString()),
    { slots: arrayUnion(data) },
    { merge: true },
  )
    .then(() => {
      return "success";
    })
    .catch((err) => {
      return err;
    });
};

export const resetpassword = async (email) => {
  return await sendPasswordResetEmail(auth, email)
    .then((response) => {
      return "success";
    })
    .catch((err) => {
      return err;
    });
};

// ✅ Save Lead Source
export const saveLeadSource = async (leadData) => {
  try {
    const docRef = doc(collection(firestored, "lead_sources"));

    await setDoc(docRef, {
      docId: docRef.id,
      abbreviation: leadData.abbreviation,
      name: leadData.name,
      leadCommission: leadData.leadCommission
        ? Number(leadData.leadCommission)
        : 0,
      createdAt: Date.now(),
    });

    return { success: true, id: docRef.id }; // ✅ success only after Firestore commit
  } catch (error) {
    console.error("Error saving lead source:", error);
    return { success: false, error: error.message };
  }
};

export const updateLeadSource = async (id, data) => {
  try {
    // Fetch existing document data
    const docRef = doc(firestored, "lead_sources", id);

    await updateDoc(docRef, data);
    return { success: true, id: id };
  } catch (error) {
    console.error("Error updating leade:", error);
    return { success: false, error: error.message };
  }
};

// ✅ Delete Lead Source by ID
export const deleteLeadSource = async (id) => {
  try {
    await deleteDoc(doc(firestored, "lead_sources", id)); // ✅ wait for Firestore delete
    return { success: true };
  } catch (error) {
    console.error("Error deleting lead source:", error);
    return { success: false, error: error.message };
  }
};

// Real-time listener
export const listenLeadSources = (callback) => {
  const q = query(
    collection(firestored, "lead_sources"),
    orderBy("createdAt", "desc"),
  );
  const unsubscribe = onSnapshot(q, (snapshot) => {
    const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    const sortedData = [...data].sort((a, b) =>
      (a.abbreviation || "").localeCompare(b.abbreviation || "", undefined, {
        sensitivity: "base",
      }),
    );
    callback(sortedData);
  });

  return unsubscribe;
};

export const getAllusers = async (table) => {
  const items = await getDocs(query(collection(firestored, table)));

  return items.docs.map((doc) => doc.data());
};
export const getAlldealforAdmin = async (table, adminid) => {
  const items = await getDocs(
    query(
      collection(firestored, table),
      // where("assignedteamlead", "==", adminid)
    ),
  );

  return items.docs
    .map((doc) => doc.data())
    .filter((d) => !(typeof d.deletedAt === "number" && d.deletedAt > 0))
    .sort((a, b) => a.closingdate.localeCompare(b.closingdate));
};

// Deal stages that mean a listing has been won (contract signed, going to market
// or further). "Closed" is treated separately as its own appointment status.
const LISTING_WON_STAGES = [
  "Pre-Market",
  "Coming Soon",
  "Under Contract",
  "Pending",
  "Active",
];

// Stages that count as "listed" — first time a deal hits one of these, we
// stamp listedDate. Pending is excluded (it's the post-UC closing window).
const LISTED_STAGES = ["Pre-Market", "Coming Soon", "Active"];

export const updateDealStageInFirebase = async (
  dealId,
  newStage,
  extraData = {}
) => {
  try {
    const dealRef = doc(firestored, "deals", dealId);

    // Stamp first-time-reached timestamps so cycle-time math works without
    // a backend stage-history table. Only set if not already present.
    const snap = await getDoc(dealRef);
    const existing = snap.exists() ? snap.data() : {};
    const stamps = {};
    const nowIso = new Date().toISOString();
    if (LISTED_STAGES.includes(newStage) && !existing.listedDate) {
      stamps.listedDate = nowIso;
    }
    if (newStage === "Under Contract" && !existing.underContractDate) {
      stamps.underContractDate = nowIso;
    }
    if (newStage === "Closed" && !existing.closedDate) {
      stamps.closedDate = nowIso;
    }

    await updateDoc(dealRef, {
      dealStage: newStage,
      ...stamps,
      ...extraData,
    });

    let newApptStatus = null;
    if (newStage === "Closed") newApptStatus = "closed";
    else if (LISTING_WON_STAGES.includes(newStage)) newApptStatus = "won";

    if (newApptStatus) {
      const apptsSnap = await getDocs(
        query(
          collection(firestored, "listingAppointments"),
          where("dealId", "==", String(dealId)),
        ),
      );
      await Promise.all(
        apptsSnap.docs.map((d) =>
          updateDoc(doc(firestored, "listingAppointments", d.id), {
            status: newApptStatus,
          }),
        ),
      );
    }
  } catch (error) {
    console.error("Error updating deal stage:", error);
  }
};

export const getSourceLead = async () => {
  const snapshot = await getDocs(collection(firestored, "lead_sources"));
  const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  console.log("🔥 Direct Firestore test:", data);
  return data;
};

export const updateIsShowStat = async (userId, value) => {
  try {
    const userRef = doc(firestored, "users", userId);
    await updateDoc(userRef, { isShowStats: value });
    console.log(`✅ isShowStat updated for user ${userId}:`, value);
    return true;
  } catch (error) {
    console.error("❌ Error updating isShowStat:", error);
    return false;
  }
};

export const deleteDealsOf2024 = async () => {
  try {
    const snapshot = await getDocs(collection(firestored, "deals"));

    if (snapshot.empty) {
      alert("No deals found");
      return;
    }

    const deletePromises = [];

    snapshot.forEach((document) => {
      const data = document.data();

      if (
        data.closingdate &&
        typeof data.closingdate === "string" &&
        data.closingdate.startsWith("2024")
      ) {
        console.log("Deleting:", document.id, data.closingdate);

        // ✅ ALWAYS use document.id
        deletePromises.push(deleteDoc(doc(firestored, "deals", document.id)));
      }
    });

    await Promise.all(deletePromises);

    alert("All 2024 deals deleted successfully 🚀");
  } catch (error) {
    console.error("Error deleting deals:", error);
  }
};

export const getUserById = async (id) => {
  const docRef = doc(firestored, "users", id);
  const docSnap = await getDoc(docRef);

  return docSnap.exists() ? docSnap.data() : null;
};

export const getIsShowStat = async (userId) => {
  try {
    const ref = doc(firestored, "users", userId);
    const snap = await getDoc(ref);

    if (snap.exists()) {
      return snap.data()?.isShowStat ?? false;
    } else {
      return false;
    }
  } catch (error) {
    console.error("Error fetching isShowStat:", error);
    return false;
  }
};

export const getUsers = async () => {
  const snapshot = await getDocs(collection(firestored, "users"));
  const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  console.log("🔥 Direct Firestore test:", data);
  return data;
};

export const uploadImageToUser = async (imageFile, path) => {
  if (!imageFile) return "";

  const storageRef = ref(storage, `${path}/${Date.now()}-${imageFile.name}`);
  const snapshot = await uploadBytes(storageRef, imageFile);
  const downloadURL = await getDownloadURL(snapshot.ref);

  return downloadURL;
};

export const saveitemwithkey = async (collection, key, chatdata) => {
  return await setDoc(doc(firestored, collection, key), chatdata, {
    merge: true,
  })
    .then(() => {
      return "success";
    })
    .catch((err) => {
      return err;
    });
};

export const getAllEmployeeTasks = async (
  table,
  parameter,
  comparison,
  value,
) => {
  const items = await getDocs(
    query(collection(firestored, table), where(parameter, comparison, value)),
  );

  return items.docs.map((doc) => doc.data());
};

export const addChatIdToDeal = async (dealId, chatId) => {
  try {
    const dealRef = doc(firestored, "deals", dealId);

    await updateDoc(dealRef, {
      chatId: chatId,
    });

    console.log("Chat ID added successfully");
  } catch (error) {
    console.error("Error adding chat ID:", error);
  }
};

export const getDealTasks = async (dealId) => {
  const dealRef = doc(firestored, "deals", dealId);
  const snap = await getDoc(dealRef);

  if (snap.exists()) {
    return snap.data().tasks || [];
  }
  return [];
};

export const addTaskToDeal = async (dealId, task) => {
  const dealRef = doc(firestored, "deals", dealId);

  const snap = await getDoc(dealRef);

  let existingTasks = [];

  if (snap.exists()) {
    existingTasks = snap.data().tasks || [];
  }

  const updatedTasks = [...existingTasks, task];

  await setDoc(dealRef, { tasks: updatedTasks }, { merge: true });
};

export const updateDealTasks = async (dealId, tasks) => {
  const ref = doc(firestored, "deals", dealId);

  await setDoc(
    ref,
    { tasks: tasks },
    { merge: true }, // ✅ VERY IMPORTANT
  );
};

export async function saveNoteToFirebase(dealId, text) {
  // Use setDoc with merge so this works even if the deal doc somehow lacks
  // a `notes` field or was created out-of-band. updateDoc would throw if the
  // doc doesn't exist; merge:true upserts safely.
  const dealRef = doc(firestored, "deals", String(dealId));
  await setDoc(dealRef, { notes: text }, { merge: true });
}

export async function getNoteFromFirebase(dealId) {
  try {
    const dealRef = doc(firestored, "deals", dealId);
    const dealSnap = await getDoc(dealRef);
    if (dealSnap.exists()) {
      return dealSnap.data().notes || ""; // fetch the "notes" key
    } else {
      return "";
    }
  } catch (error) {
    console.error("Error fetching note:", error);
    return "";
  }
}

export const getNotifications = async (userid) => {
  const snapshot = await getDocs(
    query(
      collection(firestored, "notifications"),
      where("assigneded", "==", userid),
      orderBy("notificationid", "desc"),
    ),
  );

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
};

export const markNotificationAsRead = async (id) => {
  try {
    const ref = doc(firestored, "notifications", id);
    await updateDoc(ref, {
      read: true,
    });
  } catch (error) {
    console.error("Error updating notification:", error);
  }
};

const TEMPLATE_DOC = "template";

export const getTaskTemplate = async () => {
  const ref = doc(firestored, "tasktemplate", TEMPLATE_DOC);
  const snap = await getDoc(ref);

  return snap.exists() ? snap.data() : {};
};

export const saveTaskTemplate = async (data) => {
  const ref = doc(firestored, "tasktemplate", TEMPLATE_DOC);
  await setDoc(ref, data);
};

export const addElectronicLockbox = async (data) => {
  const docRef = doc(firestored, "electronic_lockboxes", data.id);

  await setDoc(docRef, {
    ...data,
  });

  return docRef;
};

export const updateElectronicLockbox = async (id, data) => {
  if (!id) {
    throw new Error("Document ID is required for update.");
  }

  const docRef = doc(firestored, "electronic_lockboxes", id);

  await updateDoc(docRef, {
    ...data,
  });

  return docRef;
};

export const getElectronicLockboxes = async () => {
  const snap = await getDocs(collection(firestored, "electronic_lockboxes"));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const deleteElectronicLockbox = async (id) => {
  try {
    const docRef = doc(firestored, "electronic_lockboxes", id);
    await deleteDoc(docRef);
    return true;
  } catch (error) {
    console.error("Error deleting electronic lockbox:", error);
    throw error;
  }
};

export const addPushButtonLockbox = async (data) => {
  const docRef = doc(firestored, "pushbutton_lockboxes", data.id);

  await setDoc(docRef, {
    ...data,
  });

  return docRef;
};

export const updatePushButtonLockbox = async (id, data) => {
  if (!id) {
    throw new Error("Document ID is required for update.");
  }

  const docRef = doc(firestored, "pushbutton_lockboxes", id);

  await updateDoc(docRef, {
    ...data,
  });

  return docRef;
};

export const deletePushButtonLockbox = async (id) => {
  try {
    const docRef = doc(firestored, "pushbutton_lockboxes", id);
    await deleteDoc(docRef);
    return true;
  } catch (error) {
    console.error("Error deleting pushbutton lockbox:", error);
    throw error;
  }
};

export const getPushButtonLockbox = async () => {
  const snap = await getDocs(collection(firestored, "pushbutton_lockboxes"));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const addContactToFirebase = async (data) => {
  const docRef = doc(firestored, "sync_contacts", String(data.id));

  await setDoc(docRef, data);

  return docRef;
};

// GET all synced contacts
export const getAllContactsFromFirebase = async (createdAtId) => {
  const q = query(
    collection(firestored, "sync_contacts"),
    where("createdBy", "==", createdAtId),
  );

  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => ({
    ...doc.data(),
    id: doc.id,
  }));
};

export const updateContactInFirebase = async (id, data) => {
  const ref = doc(firestored, "sync_contacts", id);
  // Accept arbitrary fields (name/email/phone/notes/etc.) so callers can patch
  // a single field without clobbering the others.
  await updateDoc(ref, { ...data });
};

export const deleteContactFromFirebase = async (id) => {
  try {
    if (!id) throw new Error("Contact ID is required");

    const docRef = doc(firestored, "sync_contacts", id);
    await deleteDoc(docRef);

    return true;
  } catch (error) {
    console.error("Delete Error:", error);
    throw error;
  }
};

export const saveContactType = async (data) => {
  try {
    const docRef = doc(collection(firestored, "contact_type"));

    await setDoc(docRef, {
      id: docRef.id,
      abbreviation: data.abbreviation,
      type: data.type,
      clientType: data.selectedOption,
      createdAt: data.userId,
    });

    return { success: true, id: docRef.id }; // ✅ success only after Firestore commit
  } catch (error) {
    console.error("Error saving contact type:", error);
    return { success: false, error: error.message };
  }
};

export const updateContactType = async (id, data) => {
  try {
    // Fetch existing document data
    const docRef = doc(firestored, "contact_type", id);

    await updateDoc(docRef, data);
    return { success: true, id: id };
  } catch (error) {
    console.error("Error updating coontact type:", error);
    return { success: false, error: error.message };
  }
};

export const listenContactType = (callback) => {
  const q = query(
    collection(firestored, "contact_type"),
    orderBy("createdAt", "desc"),
  );
  const unsubscribe = onSnapshot(q, (snapshot) => {
    const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    const sortedData = [...data].sort((a, b) =>
      (a.abbreviation || "").localeCompare(b.abbreviation || "", undefined, {
        sensitivity: "base",
      }),
    );
    callback(sortedData);
  });

  return unsubscribe;
};

export const deleteContactType = async (id) => {
  try {
    await deleteDoc(doc(firestored, "contact_type", id)); // ✅ wait for Firestore delete
    return { success: true };
  } catch (error) {
    console.error("Error deleting contact type:", error);
    return { success: false, error: error.message };
  }
};

export const getContactTypes = async () => {
  try {
    const snapshot = await getDocs(collection(firestored, "contact_type"));

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error("Error fetching contact types:", error);
    return [];
  }
};

export const updateDealById = async (dealId, updatedData) => {
  try {
    const dealRef = doc(firestored, "deals", String(dealId));

    await setDoc(dealRef, updatedData, { merge: true });

    return true;
  } catch (error) {
    console.error("Error updating deal:", error);
    throw error;
  }
};

// export const updateDealById = async (dealId, updatedData) => {
//   try {
//     if (!dealId) throw new Error("Deal ID is required");

//     const dealRef = doc(firestored, "deals", dealId);

//     await updateDoc(dealRef, {
//       ...updatedData,
//     });

//     return {
//       success: true,
//       message: "Deal updated successfully",
//     };
//   } catch (error) {
//     console.error("Error updating deal:", error);

//     return {
//       success: false,
//       message: error.message,
//     };
//   }
// };

export const updatePushButtonLockboxById = async (id, data) => {
  const ref = doc(firestored, "pushbutton_lockboxes", id);
  await updateDoc(ref, data);
};

export const updateElectronicLockboxById = async (id, data) => {
  const ref = doc(firestored, "electronic_lockboxes", id);
  await updateDoc(ref, data);
};

// Hard-delete a user document (no soft delete / type flag — gone from Firestore).
export const hardDeleteUser = async (id) => {
  if (!id) return false;
  await deleteDoc(doc(firestored, "users", String(id)));
  return true;
};

// Unassign any lockbox (electronic or push-button) whose assignedDealId matches.
// Used when we detect a stale reference (e.g., the deal was deleted).
// Firestore == is type-strict, so we try the dealId as both string and number
// to cover historical data shapes.
export const unassignLockboxesByDealId = async (dealId) => {
  if (!dealId) return 0;
  let count = 0;
  const clear = {
    status: "Available",
    assignedDealId: "",
    assignedDealLabel: "",
  };
  const candidates = [];
  const asNumber = Number(dealId);
  if (!Number.isNaN(asNumber)) candidates.push(asNumber);
  candidates.push(String(dealId));
  const seenIds = new Set();
  for (const collectionName of [
    "electronic_lockboxes",
    "pushbutton_lockboxes",
  ]) {
    for (const value of candidates) {
      try {
        const snap = await getDocs(
          query(
            collection(firestored, collectionName),
            where("assignedDealId", "==", value),
          ),
        );
        for (const d of snap.docs) {
          if (seenIds.has(d.ref.path)) continue;
          seenIds.add(d.ref.path);
          await updateDoc(d.ref, clear);
          count += 1;
        }
      } catch (err) {
        console.error(
          `Failed cleaning ${collectionName} for value ${value}:`,
          err,
        );
      }
    }
  }
  return count;
};

export const getContactsByIds = async (contactIds = []) => {
  try {
    if (!contactIds.length) return [];

    const results = await Promise.all(
      contactIds.map(async (contactId) => {
        const docRef = doc(firestored, "sync_contacts", String(contactId));
        const snapshot = await getDoc(docRef);

        if (snapshot.exists()) {
          return {
            ...snapshot.data(),
            id: snapshot.id,
            firebaseId: snapshot.id,
          };
        }

        return null;
      }),
    );

    return results.filter(Boolean);
  } catch (error) {
    console.error("Error fetching contacts by IDs:", error);
    return [];
  }
};

export const getEmailReceipts = async (userId, tabType = "all") => {
  try {
    if (!userId) throw new Error("User ID is required");

    let q;

    // All records
    if (tabType === "all") {
      q = query(
        collection(firestored, "emailrecepts"),
        where("createdby", "==", userId)
      );
    }

    // Install Vendor
    else if (tabType === "installvendor") {
      q = query(
        collection(firestored, "emailrecepts"),
        where("createdby", "==", userId),
        where("type", "==", "install")
      );
    }

    // Destaging Vendor
    else if (tabType === "destagingVendor") {
      q = query(
        collection(firestored, "emailrecepts"),
        where("createdby", "==", userId),
        where("type", "==", "destaging")
      );
    }

    // Other tabs
    else {
      q = query(
        collection(firestored, "emailrecepts"),
        where("createdby", "==", userId),
        where("type", "==", tabType)
      );
    }

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error("Error fetching email receipts:", error);
    return [];
  }
};

// ============ LISTING APPOINTMENTS ============
// Each document represents one listing appointment that an agent went on
// (or has scheduled). Outcome is tracked via the `status` field.

export const getListingAppointments = async () => {
  const snap = await getDocs(query(collection(firestored, "listingAppointments")));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const saveListingAppointment = async (data) => {
  const id =
    data.id ||
    `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  await setDoc(
    doc(firestored, "listingAppointments", id),
    { ...data, id, createdAt: data.createdAt || new Date().toISOString() },
    { merge: true },
  );
  return id;
};

export const updateListingAppointment = async (id, updates) => {
  await setDoc(
    doc(firestored, "listingAppointments", id),
    updates,
    { merge: true },
  );
};

export const deleteListingAppointment = async (id) => {
  await deleteDoc(doc(firestored, "listingAppointments", id));
};

// ============ TEAM FEED POSTS ============
// Posts are stored in the `posts` collection. Reactions are a sparse map
// of userId → "like" | "dislike". reactionCounts are denormalized so the
// feed can render counts without iterating reactions.
// Note: computeNewReactionState is imported from ./feedHelpers at the top of this file.

const POSTS_COLLECTION = "posts";
const POSTS_PAGE_SIZE = 20;

// Caller can pass an explicit `id` (so callers can upload images to a known
// storage path before the post doc exists). If omitted, one is generated.
export async function createPost({
  id,
  authorId,
  authorName,
  authorRole,
  text,
  imageUrls = [],
}) {
  const postId = id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const post = {
    id: postId,
    authorId,
    authorName: authorName || "",
    authorRole: authorRole || "",
    text: text || "",
    imageUrls: Array.isArray(imageUrls) ? imageUrls : [],
    createdAt: Date.now(),
    updatedAt: null,
    reactions: {},
    reactionCounts: { ...EMPTY_REACTION_COUNTS },
    commentCount: 0,
    deleted: false,
  };
  await setDoc(doc(firestored, POSTS_COLLECTION, postId), post);
  return post;
}

export function generatePostId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

// Cursor is `{ createdAt, id }` or null. The `id` tiebreaker prevents skipping
// posts when two share the same createdAt timestamp (e.g., bulk imports).
export async function getPosts({ cursor = null, pageSize = POSTS_PAGE_SIZE } = {}) {
  const constraints = [
    where("deleted", "==", false),
    orderBy("createdAt", "desc"),
    orderBy("id", "asc"),
    limit(pageSize + 1),
  ];
  if (cursor !== null) {
    constraints.splice(3, 0, startAfter(cursor.createdAt, cursor.id));
  }
  const q = query(collection(firestored, POSTS_COLLECTION), ...constraints);
  const snap = await getDocs(q);
  const docs = snap.docs.map((d) => d.data());
  const hasMore = docs.length > pageSize;
  const posts = hasMore ? docs.slice(0, pageSize) : docs;
  const last = posts.length > 0 ? posts[posts.length - 1] : null;
  const nextCursor = last ? { createdAt: last.createdAt, id: last.id } : null;
  return { posts, nextCursor, hasMore };
}

// Count posts created strictly after `timestamp` (ms epoch). Uses an aggregate
// count query so we don't download the documents — cheap enough to poll.
export async function countPostsSince(timestamp) {
  const ts = Number(timestamp) || 0;
  const q = query(
    collection(firestored, POSTS_COLLECTION),
    where("deleted", "==", false),
    where("createdAt", ">", ts),
  );
  const snap = await getCountFromServer(q);
  return snap.data().count;
}

// Stamp "now" as the last time this user opened the Feed, so the unread badge
// resets. Stored on the user doc → consistent across the user's devices.
export async function markFeedSeen(userId) {
  if (!userId) return;
  await updateUserProfile(userId, { lastSeenFeedAt: Date.now() });
}

export async function updatePost(id, updates) {
  await updateDoc(doc(firestored, POSTS_COLLECTION, id), {
    ...updates,
    updatedAt: Date.now(),
  });
}

// Soft-delete: feed queries filter out deleted=true.
export async function deletePost(id) {
  await updateDoc(doc(firestored, POSTS_COLLECTION, id), { deleted: true });
}

// Atomic toggle so two simultaneous clicks don't corrupt counts.
// Returns the updated post.
export async function togglePostReaction(postId, userId, reaction) {
  const postRef = doc(firestored, POSTS_COLLECTION, postId);
  return await runTransaction(firestored, async (tx) => {
    const snap = await tx.get(postRef);
    if (!snap.exists()) throw new Error("Post not found");
    const post = snap.data();
    const prev = post.reactions?.[userId] || null;
    const { newReaction, deltas } = computeNewReactionState(prev, reaction);
    const newReactions = { ...(post.reactions || {}) };
    if (newReaction === null) {
      delete newReactions[userId];
    } else {
      newReactions[userId] = newReaction;
    }
    const newCounts = applyReactionDeltas(post.reactionCounts, deltas);
    tx.update(postRef, {
      reactions: newReactions,
      reactionCounts: newCounts,
    });
    return {
      ...post,
      reactions: newReactions,
      reactionCounts: newCounts,
    };
  });
}

// Upload a single image file for a feed post. Stored under the same
// dealPhotos/ path that property photo uploads use — that path is already
// covered by the deployed storage rules, so this works without needing to
// ship a new `posts/` rule.
export async function uploadPostImage(file, postId) {
  if (!file) throw new Error("No file provided");
  const safeName = `${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
  const path = `dealPhotos/feed/${postId}/${safeName}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file);
  return await getDownloadURL(storageRef);
}

// ============ TEAM FEED COMMENTS ============
// Comments live in posts/{postId}/comments sub-collection. The parent post's
// commentCount is kept in sync via a transaction.

export async function getCommentsForPost(postId) {
  const q = query(
    collection(firestored, POSTS_COLLECTION, postId, "comments"),
    where("deleted", "==", false),
    orderBy("createdAt", "asc"),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data());
}

export async function addComment(postId, { authorId, authorName, authorRole, text }) {
  const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const comment = {
    id,
    postId,
    authorId,
    authorName: authorName || "",
    authorRole: authorRole || "",
    text: text || "",
    createdAt: Date.now(),
    updatedAt: null,
    deleted: false,
  };
  const commentRef = doc(firestored, POSTS_COLLECTION, postId, "comments", id);
  const postRef = doc(firestored, POSTS_COLLECTION, postId);
  await runTransaction(firestored, async (tx) => {
    const postSnap = await tx.get(postRef);
    if (!postSnap.exists()) throw new Error("Post not found");
    const post = postSnap.data();
    tx.set(commentRef, comment);
    tx.update(postRef, { commentCount: (post.commentCount || 0) + 1 });
  });
  return comment;
}

export async function updateComment(postId, commentId, updates) {
  await updateDoc(
    doc(firestored, POSTS_COLLECTION, postId, "comments", commentId),
    { ...updates, updatedAt: Date.now() },
  );
}

// Soft-delete; decrement commentCount on the parent post.
export async function deleteComment(postId, commentId) {
  const commentRef = doc(firestored, POSTS_COLLECTION, postId, "comments", commentId);
  const postRef = doc(firestored, POSTS_COLLECTION, postId);
  await runTransaction(firestored, async (tx) => {
    const commentSnap = await tx.get(commentRef);
    if (!commentSnap.exists()) return;
    const comment = commentSnap.data();
    if (comment.deleted) return;
    const postSnap = await tx.get(postRef);
    tx.update(commentRef, { deleted: true });
    if (postSnap.exists()) {
      const post = postSnap.data();
      tx.update(postRef, {
        commentCount: Math.max(0, (post.commentCount || 0) - 1),
      });
    }
  });
}

// ==== Open Houses ====

// Resolves the first customer on a deal into { name, email }. Handles both:
//   - modern shape: customers = ["contactId", ...] (looked up in sync_contacts)
//   - legacy shape: customers = [{ clientName, name?, email?, ... }, ...]
async function resolveSellerFromDeal(deal) {
  const first = deal?.customers?.[0];
  if (!first) return { name: "", email: "" };
  if (typeof first === "object") {
    return {
      name: first.name || first.clientName || "",
      email: first.email || first.clientEmail || "",
    };
  }
  const contacts = await getContactsByIds([first]);
  const c = contacts[0];
  return { name: c?.name || "", email: c?.email || "" };
}

export const createOpenHouse = async ({ deal, fields, userId }) => {
  const ref = doc(collection(firestored, "openHouses"));
  const seller = await resolveSellerFromDeal(deal);
  const lockbox = await resolveLockboxFromDeal(deal);
  const payload = {
    id: ref.id,
    dealId: deal.id,
    propertyAddress: deal.propertyaddress || "",
    sellerName: seller.name,
    sellerEmail: seller.email,
    date: fields.date,
    startTime: fields.startTime,
    endTime: fields.endTime,
    hostingAgentId: fields.hostingAgentId || "",
    hostingAgentName: fields.hostingAgentName,
    hostingAgentEmail: fields.hostingAgentEmail,
    notes: fields.notes || "",
    // Denormalized lockbox info so the hosting-agent email can show
    // how to get into the property without a second deal lookup.
    lockboxType: lockbox.type,
    lockboxNumber: lockbox.number,
    lockboxCode: lockbox.code,
    status: "Scheduled",
    // True when the home is occupied and we're holding the scheduling
    // notifications until the user confirms the seller verbally OK'd the
    // time. False (or absent) means notifications can / have gone out.
    pendingSellerApproval: !!fields.pendingSellerApproval,
    feedbackToken: generateFeedbackToken(),
    feedback: null,
    pendingRecap: false,
    recapSentAt: null,
    recapSentBy: null,
    cancelReason: null,
    cancelledAt: null,
    cancelledBy: null,
    createdAt: serverTimestamp(),
    createdBy: userId,
    updatedAt: serverTimestamp(),
  };
  await setDoc(ref, payload);
  return { id: ref.id };
};

// Resolve lockbox info from a deal — looks up the actual lockbox doc in
// either pushbutton_lockboxes or electronic_lockboxes by id. Returns
// empty strings when the deal has no lockbox assigned.
async function resolveLockboxFromDeal(deal) {
  const type = deal?.lockboxType || "";
  const lockboxId = deal?.selectedLockbox;
  if (!type || !lockboxId) {
    return { type: "", number: "", code: "" };
  }
  try {
    const collectionName =
      type === "Push Button" ? "pushbutton_lockboxes" : "electronic_lockboxes";
    const snap = await getDoc(doc(firestored, collectionName, String(lockboxId)));
    if (!snap.exists()) return { type, number: "", code: "" };
    const data = snap.data() || {};
    const code = type === "Push Button" ? data.code : data.pin;
    return {
      type,
      number: data.lockboxNumber || "",
      code: code || "",
    };
  } catch (err) {
    console.error("resolveLockboxFromDeal failed:", err);
    return { type, number: "", code: "" };
  }
}

export const updateOpenHouse = async (id, patch, userId) => {
  const ref = doc(firestored, "openHouses", id);
  await updateDoc(ref, {
    ...patch,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  });
};

export const cancelOpenHouse = async (id, reason, userId) => {
  const ref = doc(firestored, "openHouses", id);
  await updateDoc(ref, {
    status: "Cancelled",
    cancelReason: reason || null,
    cancelledAt: serverTimestamp(),
    cancelledBy: userId,
    updatedAt: serverTimestamp(),
  });
};

export const submitOpenHouseFeedbackInApp = async (id, feedback, userId) => {
  const ref = doc(firestored, "openHouses", id);
  await updateDoc(ref, {
    feedback: {
      ...feedback,
      submittedAt: serverTimestamp(),
      submittedVia: "app",
      submittedByUserId: userId,
    },
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  });
};

export const deleteOpenHouse = async (id) => {
  await deleteDoc(doc(firestored, "openHouses", id));
};

// Distinct outside hosting agents (not team members) seen on past open
// houses. Used to autocomplete the ScheduleModal name field so the team
// doesn't re-type the same showing agent every time. Returns
// [{ name, email, lastUsedAt }] sorted most-recent first.
export const getOutsideHostingAgents = async () => {
  try {
    const snap = await getDocs(collection(firestored, "openHouses"));
    const seen = new Map(); // key: email lowercased → { name, email, lastUsedAt }
    snap.forEach((d) => {
      const oh = d.data() || {};
      if (oh.hostingAgentId) return; // team member, not outside
      const email = String(oh.hostingAgentEmail || "").trim();
      const name = String(oh.hostingAgentName || "").trim();
      if (!email || !name) return;
      const key = email.toLowerCase();
      const ts = oh.createdAt?.toDate
        ? oh.createdAt.toDate().getTime()
        : (typeof oh.createdAt === "number" ? oh.createdAt : 0);
      const existing = seen.get(key);
      if (!existing || ts > existing.lastUsedAt) {
        seen.set(key, { name, email, lastUsedAt: ts });
      }
    });
    return Array.from(seen.values()).sort((a, b) => b.lastUsedAt - a.lastUsedAt);
  } catch (err) {
    console.error("getOutsideHostingAgents failed:", err);
    return [];
  }
};

export const subscribeOpenHouses = (callback) => {
  const q = query(collection(firestored, "openHouses"));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
};

export const subscribeOpenHousesForDeal = (dealId, callback) => {
  const q = query(collection(firestored, "openHouses"), where("dealId", "==", dealId));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
};

export const subscribeEmailLog = (callback) => {
  const q = query(
    collection(firestored, "emailLog"),
    orderBy("sentAt", "desc")
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
};

export const getEligibleListingsForOpenHouse = async () => {
  const snap = await getDocs(collection(firestored, "deals"));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};
